import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect } from "react";

import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";

import { DefinitionTable } from "src/components/Legal/DefinitionTable";
import { HighlightedSection, TopLink } from "src/components/Legal/legalStyles";
import { ContactUsLink, Link } from "src/components/Link";
import { ArticlePageContent } from "src/components/layout/ArticlePage";
import { DefaultPageLayout } from "src/components/layout/DefaultPageLayout";
import { HeaderTitlePageSection } from "src/components/layout/PageSection";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { LinkAppearance } from "src/styles/link";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { logger } from "src/utility/logger";
import { getWindow } from "src/utility/window";

const LAST_UPDATED_STRING = "08/26/2021";

const SECTION_HEADERS: { [key: string]: string } = {
  givers: "The Basics for Givers",
  nonprofits: "The Basics for Nonprofits",
};

const ROUGH_HEADER_HEIGHT = "104px";

/**
 * This hidden div serves as an anchor for  links to scroll to. We use a
 * negative offset for the space that the header takes up.
 */
export const SectionAnchor = styled.div`
  display: block;
  position: relative;
  visibility: hidden;
  top: 0;

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      top: -${ROUGH_HEADER_HEIGHT};
    `,
  })}
`;

export const useScrollToAnchor = () => {
  const { hash } = useEdoRouter();

  useEffect(() => {
    if (hash) {
      try {
        getWindow()?.document.querySelector(hash)?.scrollIntoView();
      } catch (error) {
        logger.warn({
          message: "Your browser does not support this link",
          error,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const TermsOfService: React.FCC = () => {
  useScrollToAnchor();

  return (
    <DefaultPageLayout
      pageTitle={"Terms of Service"}
      headerContent={
        <HeaderTitlePageSection>
          <h1>Terms of Service</h1>
        </HeaderTitlePageSection>
      }
    >
      <ArticlePageContent>
        <ul>
          {Object.keys(SECTION_HEADERS).map((key) => (
            <li key={key}>
              <Link
                data-tname={key}
                to={`#${key}`}
                appearance={LinkAppearance.HYPERLINK}
              >
                {SECTION_HEADERS[key]}
              </Link>
            </li>
          ))}
        </ul>
        <p>
          Welcome! We’re Every.org and we’re so happy to have you as our guest.
          By accessing the Site (defined below) and/or using any of the Services
          (also defined below) you’re agreeing to these legally binding rules,
          aka the “Terms.” You’re also agreeing to our{" "}
          <Link
            data-tname="privacyPolicyLink"
            target="privacy"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.PRIVACY_POLICY,
            })}
          >
            Privacy Policy
          </Link>
          , which is incorporated into and a part of these Terms.
        </p>
        <p>
          You acknowledge that these Terms are supported by reasonable and
          valuable consideration, and you acknowledge the receipt and
          sufficiency of that consideration.
        </p>
        <p>
          If you do not accept these Terms, and/or if you are not of legal age
          to form a binding contract with Every.org, you may not use the Sites
          or the Services.
        </p>
        <p>
          Finally, you should note that we may offer special services or
          features from time to time that have their own terms and conditions.
          In those cases, if the terms specific to the special feature or
          service conflict with these Terms, the terms specific to the special
          feature or service will control.
        </p>
        <p>With that, let’s get started!</p>
        <p>
          We aim for clarity in these Terms so that they will be easy to follow.
          These Terms were last updated on {LAST_UPDATED_STRING}, and will be
          updated on an ongoing basis, as necessary. We will be sure to let you
          know when we make material changes with a notification on your next
          visit to the Site. You agree that we may notify you by posting them on
          the Site, and that your use of the Site and/or Services after the
          effective date of the updated Terms will constitute agreement to the
          updated Terms.
        </p>

        <p>
          Before we dive in, here are some definitions that we will use
          throughout this document:
        </p>
        <DefinitionTable />
        <p>
          These Terms govern your use of the Site, which as you can see above,
          means both our website and all of our mobile apps (real or imagined).
          If you do not agree to be bound by these Terms, you may not use or
          access the Site or Services.
        </p>
        <SectionAnchor id="givers" />
        <h2>{SECTION_HEADERS["givers"]}</h2>
        <p>
          Every.org is a service designed to make supporting your favorite
          causes as easy as possible, and to facilitate a community built on
          giving. Our Site is meant solely for personal, non-commercial use.
        </p>
        <p>
          In order to make full use of the Site, you will need to register for
          an account. For more information about the account-creation process,
          including what information we collect and how we use it, please
          consult our{" "}
          <Link
            data-tname="privacyPolicyLink"
            target="privacy"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.PRIVACY_POLICY,
            })}
          >
            Privacy Policy
          </Link>
          .
        </p>
        <h3>Donations</h3>
        <p>
          We have selected a wide variety of nonprofits to make it easy for
          generous souls like you to support your favorite causes. Using the
          Site, you can make gifts of support to all (or nearly all) of the
          nonprofits listed.
        </p>
        <p>
          When you make a gift of support to a Nonprofit through the Site, your
          Donation goes directly to Every.org first, which has exclusive legal
          control over the Donation, and we issue you a receipt for your
          charitable contribution as a US 501(c)(3) nonprofit. At the time you
          make a Donation to Every.org, you also tell us which Nonprofit you’re
          hoping to support with that Donation. On a regular basis, Every.org
          approves your recommendation and disburses funds collected through
          Donations to recipient Nonprofits (either directly, or through a
          third-party nonprofit partner). We strive to ensure that your Donation
          goes to the Nonprofit that you preferred for your gift of support.
          This process ensures your eligibility for a tax deduction and
          consolidated records of your giving, and reduces the administrative
          burden of the Nonprofits to process multiple small donations from
          hundreds of donors.
        </p>
        <p>
          If circumstances make Giver to that particular Nonprofit impossible or
          inappropriate (such as if the selected Nonprofit ceases operations or
          loses its tax-exempt status), we will attempt to contact you first for
          additional preferences on where you would like your Donation to go. If
          we do not receive your preferences before we must disburse the funds,
          then we may in our sole discretion select an alternate Nonprofit to
          receive the Donation. We will do our best to direct your Donation to a
          Nonprofit in a similar space and/or with similar goals. Except in
          cases of fraud, we do not issue refunds.
        </p>
        <p>
          When you Donate through the Site, you may do so by either making a
          Recurring Donation or by making a One-Time Donation. You must provide
          a valid payment method to make a Donation using the Site. By providing
          your Payment Provider information, you agree that Every.org (through
          its third party payment service providers) is authorized to charge
          your Payment Provider for the full amount of any Donations you agree
          to make using the Site and that no additional notice or consent is
          required for such charges. You agree to notify us of any change in
          your billing address or payment account information used for payment
          hereunder.
        </p>
        <p>
          If you would like to cancel a Recurring Donation, you may do so at any
          time by updating your settings on the Site. If the details of your
          Recurring Donation change, for example if we remove a nonprofit from
          our system, we will notify you and obtain your instructions prior to
          charging your next Recurring Donation payment.
        </p>
        <SectionAnchor id="nonprofits" />
        <h2>{SECTION_HEADERS["nonprofits"]}</h2>
        <p>
          Every.org is not a just a service for Givers, it is designed to make
          receiving support as easy as possible for Nonprofits as well, and to
          give Givers a community in which to promote Nonprofits they care
          about.
        </p>
        <p>
          In order to receive Disbursements from us via the Site, you will need
          to meet the following minimum requirements:
        </p>
        <ul>
          <li>
            Your organization is recognized by the IRS as exempt from federal
            income tax under Internal Revenue Code Section 501(c)(3), and has
            public charity status under Section 509(a)(1) or (2), and such
            recognition is not currently revoked.
          </li>
          <li>
            If your organization does not meet that requirement, and instead
            your organization is an International Nonprofit, then you must
            submit all documentation requested by Every.org or one of our
            processing partners, and we must have determined that your
            organization is conducting charitable activities and/or is the
            equivalent of a US 501(c)(3) public charity.
          </li>
        </ul>
        <h3>Disbursements & Payment Processing</h3>
        <p>
          On a regular basis, Every.org approves Giver recommendations to
          support Nonprofits, and disburses funds actually collected through
          Donations to recipient Nonprofits (either directly, or through a
          third-party nonprofit partner). All Donations are contributions to
          Every.org, not to your organization, so you do not need to issue a
          receipt to any Giver, and we also do not need a receipt so please do
          not provide any.
        </p>
        <p>
          We use payment processing partners to receive Donations and make
          Disbursements. The amount your organization receives from Donations
          may be reduced by the fees of such payment processing partners
          depending on which payment method was chosen by the Giver. Your
          organization must provide sufficient information to such payment
          processing partners in order to receive Disbursements, and must update
          such information as needed.
        </p>
        <p>
          For more information about timing and fees, please consult our{" "}
          <Link
            data-tname="disbursementsLink"
            target="disbursements"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.DISBURSEMENTS,
            })}
          >
            Disbursements
          </Link>{" "}
          page.
        </p>
        <p>
          If we have chosen to make a Disbursement to your organization via a
          third-party nonprofit partner, you are required to enroll in the
          partner’s program and agree to any of their terms or requirements. The
          amount your organization receives from Donations may be reduced by the
          fees of such partner.
        </p>
        <p>In exchange for Disbursements, you agree and commit that:</p>
        <ul>
          <li>
            You consent to the use of the name and certain limited,
            publicly-available information about your organization on the Site,
            and permit us to fundraise and collect donations on your behalf,
            acknowledging that it is not guaranteed that any Giver will request
            a Donation to your organization, and that we have no obligation to
            list you, or to fundraise and collect donations for you at any time.
          </li>
          <li>
            You will use all Disbursements either (a) for your organization’s
            charitable purposes or (b) for any specific charitable purpose you
            have proposed to us, and we have approved, prior to collecting any
            Donations for such purposes.
          </li>
          <li>
            You will not use any Disbursement to conduct non-charitable
            activities, including illegal activities, impermissible political
            activities, or to provide an impermissible private benefit to any
            individual or entity.
          </li>
          <li>
            You will comply with all applicable state and local statutes
            governing the solicitation of charitable solicitations, including
            but not limited to fulfilling any registration requirements.
          </li>
          <li>
            The Giver who recommended their Donation support your organization
            will not receive any goods or services of value (such as
            memberships, merchandise, or event tickets) as a result of the
            Donation or Disbursement to your organization, except where such
            goods or services can be disregarded in accordance with IRS
            guidance.
          </li>
          <li>
            You will notify us promptly of any change to your tax-exempt status
            or ability to carry out the specific charitable purposes for which
            you have received Disbursements.
          </li>
        </ul>
        <p>
          If circumstances make Disbursement to your organization impossible or
          inappropriate (such as if your organization ceases operations, loses
          its tax-exempt status, or fails to comply with these Terms), we may in
          our sole discretion select an alternate Nonprofit to receive the
          Donation. Except in cases of fraud, we do not issue refunds to Givers.
          However, if we have made Direct Disbursements to your organization, we
          have the right to deduct amounts from your future Direct Disbursements
          to cover credit card charge-backs and disputed charges related to a
          Donation recommended for your organization. We will attempt to contact
          the Giver of any such Donation to let them know that a charge back or
          disputed charge will require you to refund their Disbursement to us.
          We reserve all rights to pursue charge back and disputed charge
          amounts from the Nonprofit.
        </p>
        <h3>Giver Data</h3>
        <p>
          When a Giver makes a Donation, they are given the option to share
          their information with your organization. If they choose this option,
          Every.org will make available the Giver’s name, contact information,
          and Donation amount. We may send this information in a periodic
          fundraising report or to our Disbursement partner to send to you. You
          can also request this information by contacting{" "}
          <Link
            data-tname="emailSupport"
            to="mailto:nonprofits@every.org"
            appearance={LinkAppearance.HYPERLINK}
          >
            nonprofits@every.org
          </Link>
          .
        </p>
        <p>
          For more information about what information we collect and how we use
          it, please consult our{" "}
          <Link
            data-tname="privacyPolicyLink"
            target="privacy"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.PRIVACY_POLICY,
            })}
          >
            Privacy Policy
          </Link>
          .
        </p>
        <h2>Intellectual Property Rights Generally</h2>
        <p>
          All trademarks, service marks, and trade names on the Site, including
          the Every.org trademark and the Every.org logo trademark are
          trademarks or registered trademarks of, and are proprietary to,
          Every.org or other owners that have granted Every.org permission to
          use their trademarks.
        </p>
        <p>
          Every.org owns all rights, title, and interest in the Site, all text,
          content, graphics, interfaces, code and materials, the look and feel,
          selection and arrangement, design, and organization of the Site, and
          the compilation of the content, code, data, and materials on the Site,
          including all intellectual property and proprietary rights. The Site,
          Services, and all content contained in them are copyrighted and are
          the property of Every.org. We may change the Site or Services or
          delete the content or features of either at any time, in any way, for
          any or no reason.
        </p>
        <p>
          You may not copy, download, use, redesign, reconfigure, or retransmit
          anything from the Site without Every.org’s express prior written
          consent and, if applicable, express prior written consent from the
          holder of the rights to the intellectual property (in cases where the
          property in question is not owned by Every.org). We reserve all rights
          that are not expressly granted to you under these Terms or by law. You
          may not remove, obscure, or alter any legal notices displayed in or
          along with the Site.
        </p>
        <h2>User-Generated Content (“UGC”)</h2>
        <p>
          You are solely responsible for UGC. We’ve defined the term above, but
          for the sake of clarity we repeat it here, UGC is:{" "}
          <strong>
            any content created by you that you publicly post on the Site
          </strong>
          . We act merely as a passive conduit for your online Disbursement and
          publication of your UGC. You acknowledge and agree that Every.org is
          not involved in the creation or development of UGC, disclaims any
          responsibility for UGC, and cannot be liable for claims arising out of
          or relating to UGC. Further, you acknowledge and agree that Every.org
          has no obligation to monitor, review, or remove UGC, but reserves the
          right to limit or remove UGC on the Site at its sole discretion.
        </p>
        <p>
          You hereby grant Every.org a non-exclusive, worldwide, perpetual,
          irrevocable, royalty-free, sub-licensable right to exercise all
          copyrights, publicity rights, and any other rights you have in your
          UGC, in any media now known or not currently known in order to perform
          and improve upon the Every.org.
        </p>
        <p>
          By posting UGC, you warrant and represent that you have the lawful
          authority to grant the rights set out above.
        </p>
        <h2>Digital Millennium Copyright Act</h2>
        <p>
          Every.org has adopted the following policy towards copyright
          infringement in accordance with the Digital Millennium Copyright Act
          (the {"DMCA"}). If you believe any materials provide on or in
          connection with the Site infringes upon your intellectual property
          rights, please notify us by providing the following information:
        </p>
        <ul>
          <li>
            A physical or electronic signature of a person authorized to act on
            behalf of the owner of an exclusive right that is allegedly
            infringed;
          </li>
          <li>
            Identification of the copyrighted work claimed to have been
            infringed, or, if multiple copyrighted works at a single online site
            are covered by a single notification, a representative list of such
            works;
          </li>
          <li>
            Identification of the material claimed to be infringing or to be the
            subject of infringing activity and that is to be removed or access
            disabled and information reasonably sufficient to permit the service
            provider to locate the material;
          </li>
          <li>
            Information reasonably sufficient to permit the service provider to
            contact you, such as an address, telephone number, and, if
            available, an electronic mail;
          </li>
          <li>
            A statement that you have a good faith belief that use of the
            material in the manner complained of is not authorized by the
            copyright owner, its agent, or the law; and
          </li>
          <li>
            A statement that, under penalty of perjury, the information in the
            notification is accurate and you are authorized to act on behalf of
            the owner of the exclusive right that is allegedly infringed.
          </li>
        </ul>
        <p>
          Please send the above information to{" "}
          <Link
            data-tname="emailLegal"
            to="mailto:legal@every.org"
            appearance={LinkAppearance.HYPERLINK}
          >
            legal@every.org
          </Link>
          , or by mail to us at
        </p>
        <p>
          DMCA Agent
          <br />
          Every.org
          <br />
          58 West Portal Ave #781
          <br />
          San Francisco, CA 94127
        </p>
        <p>
          We may give you notice that we have removed or disabled access to
          certain material by means of a general notice on the Site, electronic
          mail to a user’s e-mail address in our records, or by written
          communication sent by first-class mail to your physical address in our
          records. If you receive such a notice, you may provide
          counter-notification in writing to the designated agent pursuant to
          instructions that will be included in the notice.
        </p>
        <h2>License to Use the Site</h2>
        <p>
          Subject to your compliance with these Terms, Every.org grants you a
          limited, non-exclusive, non- sublicensable, revocable,
          non-transferrable license to:
        </p>
        <ul>
          <li>access and use the Site; and</li>
          <li>
            access and use any content, information, and related materials that
            may be made available through the Site, in each case solely for your
            personal, noncommercial use
          </li>
        </ul>
        <p>
          Any rights not expressly granted herein are reserved by Every.org and
          its licensors.
        </p>
        <h2>Code of Conduct</h2>
        <p>You agree to:</p>
        <ul>
          <li>
            comply with all applicable laws and regulations, including without
            limitation, privacy laws, intellectual property laws, and regulatory
            requirements.
          </li>
        </ul>
        <p>You agree that you will not:</p>
        <ul>
          <li>
            act in an unlawful or unprofessional manner including being
            dishonest, abusive, insulting, threatening, discriminatory;
          </li>
          <li>
            promote or encourage racism, sexism, homophobia, transphobia, or any
            other form of hatred or bigotry;
          </li>
          <li>misrepresent your identity;</li>
          <li>encourage or engage in any illegal activity;</li>
          <li>commit fraud;</li>
          <li>
            infringe on any third party’s rights (including, without limitation,
            intellectual property right and privacy rights); and
          </li>
          <li>
            represent yourself as being employed, an agent of, directly engaged
            by, or affiliated with Every.org.
          </li>
        </ul>
        <p>
          If you need help dealing with another Person on the Site, or have any
          other concerns relating to our Code of Conduct, please let us know by{" "}
          <ContactUsLink>contacting us</ContactUsLink> and letting us know what
          happened. We take all allegations of abuse of any kind seriously.
        </p>
        <p>
          Since it is illegal, it should go without saying, but just in case it
          wasn’t already clear: you may not hack, clone, or otherwise tamper
          with or steal any aspect of the Site, nor interfere with its
          operations.
        </p>
        <h2>App Updates and Upgrades</h2>
        <p>
          By installing any app associated with the Site, you consent to the
          installation of the app and any updates or upgrades that are released
          by Every.org on your device.
        </p>
        <p>
          Such app (including any updates or upgrades) may (i) cause your device
          to automatically communicate with Every.org’s servers to deliver
          functionality and to record usage metrics, (ii) affect app-related
          preferences or data stored on your device, and (iii) collect personal
          information as set out in our{" "}
          <Link
            data-tname="privacyPolicyLink"
            target="privacy"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.PRIVACY_POLICY,
            })}
          >
            Privacy Policy
          </Link>
          . You can uninstall the app at any time.
        </p>
        <h2>Disclaimer and Limitation of Liability</h2>
        <HighlightedSection>
          <p>
            The site is provided to you {`"as is" and "as available"`} without
            warranty of any kind, either express or implied, including but not
            limited to, fitness for a particular purpose, title, or
            non-infringement.
          </p>
          <p>
            Should applicable law not permit the foregoing exclusion of express
            or implied warranties, then we grant the minimum express or implied
            warranty required by applicable law. No advice or information,
            whether oral or written, shall create any warranty, representation
            or guarantee not expressly stated in this section.
          </p>
          <p>
            Additionally, we do not make any warranties that the site will be
            uninterrupted, secure or error free or that your use of the site
            will meet your expectations, or that the site, or any portion
            thereof, is correct, accurate, or reliable. Your use of the site is
            at your own risk. you are solely responsible for your interactions
            with other people. Every.org is not responsible for the conduct of
            any user. Every.org does not conduct criminal background checks on
            its people.
          </p>
          <p>
            Every.org will not be liable for any damages, direct, indirect,
            incidental, consequential, special, or punitive, including, without
            limitation, loss of data, income, profit or goodwill, loss of or
            damage to property and claims of third parties arising out of your
            access to or use of the site, however caused, whether based on
            breach of contract, tort (including negligence), proprietary rights
            infringement, product liability or otherwise.
          </p>
          <p>
            The foregoing shall apply even if we were advised of the possibility
            of such damages. If you become dissatisfied in any way with the
            site, your sole and exclusive remedy is to stop your use of the
            site.
          </p>
          <p>
            You hereby waive any and all claims arising out of your use of the
            site. Because some states do not allow the disclaimer of implied
            warranties or the exclusion or limitation of certain types of
            damages, these provisions may not apply to you. If any portion of
            this limitation on liability is found to be invalid or unenforceable
            for any reason, then our aggregate liability shall not exceed one
            hundred dollars ($100).
          </p>
          <p>
            The limitation of liability herein is a fundamental element of the
            basis of the bargain and reflects a fair allocation of risk. The
            services would not be provided without such limitations and you
            agree that the limitations and exclusions of liability, disclaimers
            and exclusive remedies specified herein will survive even if found
            to have failed in their essential purpose.
          </p>
        </HighlightedSection>
        <h2>Indemnification</h2>
        <p>
          All the actions you make and information you post on the Site, , and
          (for Nonprofits) all activities you conduct using Disbursements,
          remain your responsibility. Therefore, you agree to indemnify, defend,
          release, and hold us, and our partners, licensors, affiliates,
          contractors, officers, directors, employees, representatives and
          agents, harmless, from and against any third-party claims, damages
          (actual and/or consequential), actions, proceedings, demands, losses,
          liabilities, costs and expenses (including reasonable legal fees)
          suffered or reasonably incurred by us arising as a result of, or in
          connection with:
        </p>
        <ul>
          <li>any negligent acts, omissions or willful misconduct by you;</li>
          <li>your access to and use of the Site;</li>
          <li>
            (for Nonprofits) your receipt of or failure to receive Disbursements
            via the Site;
          </li>
          <li>
            any breach of these Terms or our Privacy Policy by you; and/or
          </li>
          <li>
            your violation of any law or of any rights of any third party.
          </li>
        </ul>
        <p>
          We retain the exclusive right to settle, compromise and pay any and
          all claims or causes of action which are brought against us without
          your prior consent. If we ask, you will co-operate fully and
          reasonably as required by us in the defense of any relevant claim.
        </p>
        <h2>Dispute Resolution</h2>
        <p>
          The following Arbitration Agreement contains procedures for{" "}
          <b>mandatory binding arbitration and a class action waiver.</b>
        </p>
        <ol type="a">
          <li>
            <u>Applicability of Arbitration Agreement.</u>
            <p>
              All claims and disputes (excluding claims for injunctive or other
              equitable relief as set forth below) in connection with the Terms
              or the use of any product or service provided by Every.org that
              cannot be resolved informally or in small claims court shall be
              resolved by binding arbitration on an individual basis under the
              terms of this Arbitration Agreement. Unless otherwise agreed to,
              all arbitration proceedings shall be held in English. This
              Arbitration Agreement applies to you and Every.org, and to any
              subsidiaries, affiliates, agents, employees, predecessors in
              interest, successors, and assigns, as well as all authorized or
              unauthorized users or beneficiaries of services or goods provided
              under the Terms.
            </p>
          </li>
          <li>
            <u>Notice Requirement and Informal Dispute Resolution.</u>
            <p>
              Before either party may seek arbitration, the party must first
              send to the other party a written Notice of Dispute (“Notice”)
              describing the nature and basis of the claim or dispute, and the
              requested relief. A Notice to Every.org should be sent to: 58 WEST
              PORTAL AVENUE, #781, CA 94127 US. After the Notice is received,
              you and Every.org may attempt to resolve the claim or dispute
              informally. If you and Every.org do not resolve the claim or
              dispute within thirty (30) days after the Notice is received,
              either party may begin an arbitration proceeding. The amount of
              any settlement offer made by any party may not be disclosed to the
              arbitrator until after the arbitrator has determined the amount of
              the award, if any, to which either party is entitled.
            </p>
          </li>
          <li>
            <u>Arbitration Rules.</u>
            <p>
              Arbitration shall be initiated through the American Arbitration
              Association (“AAA”), an established alternative dispute resolution
              provider (“ADR Provider”) that offers arbitration as set forth in
              this section. If AAA is not available to arbitrate, the parties
              shall agree to select an alternative ADR Provider. The rules of
              the ADR Provider shall govern all aspects of the arbitration,
              including but not limited to the method of initiating and/or
              demanding arbitration, except to the extent such rules are in
              conflict with the Terms. The AAA Consumer Arbitration Rules
              (“Arbitration Rules”) governing the arbitration are available
              online at www.adr.org or by calling the AAA at 1-800- 778-7879.
              The arbitration shall be conducted by a single, neutral
              arbitrator. Any claims or disputes where the total amount of the
              award sought is less than Ten Thousand U.S. Dollars (US
              $10,000.00) may be resolved through binding non-appearance- based
              arbitration, at the option of the party seeking relief. For claims
              or disputes where the total amount of the award sought is Ten
              Thousand U.S. Dollars (US $10,000.00) or more, the right to a
              hearing will be determined by the Arbitration Rules. Any hearing
              will be held in a location within 100 miles of your residence,
              unless you reside outside of the United States, and unless the
              parties agree otherwise. If you reside outside of the U.S., the
              arbitrator shall give the parties reasonable notice of the date,
              time and place of any oral hearings. Any judgment on the award
              rendered by the arbitrator may be entered in any court of
              competent jurisdiction. If the arbitrator grants you an award that
              is greater than the last settlement offer that Every.org made to
              you prior to the initiation of arbitration, Every.org will pay you
              the greater of the award or $2,500.00. Each party shall bear its
              own costs (including attorney’s fees) and disbursements arising
              out of the arbitration and shall pay an equal share of the fees
              and costs of the ADR Provider.
            </p>
          </li>
          <li>
            <u>Additional Rules for Non-Appearance Based Arbitration.</u>
            <p>
              If non-appearance based arbitration is elected, the arbitration
              shall be conducted by telephone, online and/or based solely on
              written submissions; the specific manner shall be chosen by the
              party initiating the arbitration. The arbitration shall not
              involve any personal appearance by the parties or witnesses unless
              otherwise agreed by the parties.
            </p>
          </li>
          <li>
            <u>Time Limits.</u>
            <p>
              If you or Every.org pursue arbitration, the arbitration action
              must be initiated and/or demanded within the statute of
              limitations (i.e., the legal deadline for filing a claim) and
              within any deadline imposed under the AAA Rules for the pertinent
              claim.
            </p>
          </li>
          <li>
            <u>Authority of Arbitrator.</u>
            <p>
              If arbitration is initiated, the arbitrator will decide the rights
              and liabilities, if any, of you and Every.org, and the dispute
              will not be consolidated with any other matters or joined with any
              other cases or parties. The arbitrator shall have the authority to
              grant motions dispositive of all or part of any claim. The
              arbitrator shall have the authority to award monetary damages, and
              to grant any non-monetary remedy or relief available to an
              individual under applicable law, the AAA Rules, and the Terms. The
              arbitrator shall issue a written award and statement of decision
              describing the essential findings and conclusions on which the
              award is based, including the calculation of any damages awarded.
              The arbitrator has the same authority to award relief on an
              individual basis that a judge in a court of law would have. The
              award of the arbitrator is final and binding upon you and
              Every.org.
            </p>
          </li>
          <li>
            <u>Waiver of Jury Trial.</u>
            <p>
              <b>
                The parties hereby waive their constitutional and statutory
                rights to go to court and have a trial in front of a judge or a
                jury,
              </b>{" "}
              instead electing that all claims and disputes shall be resolved by
              arbitration under this Arbitration Agreement. Arbitration
              procedures are typically more limited, more efficient and less
              costly than rules applicable in a court and are subject to very
              limited review by a court. In the event any litigation should
              arise between you and Every.org in any state or federal court in a
              suit to vacate or enforce an arbitration award or otherwise,{" "}
              <b>you and Every.org waive all rights to a jury trial</b>, instead
              electing that the dispute be resolved by a judge.
            </p>
          </li>
          <li>
            <u>Waiver of Class or Consolidated Actions.</u>
            <p>
              {" "}
              <b>
                All claims and disputes within the scope of this arbitration
                agreement must be arbitrated or litigated on an individual basis
                and not on a class basis, and claims of more than one customer
                or user cannot be arbitrated or litigated jointly or
                consolidated with those of any other customer or user.
              </b>
            </p>
          </li>
          <li>
            <u>Confidentiality.</u>
            <p>
              All aspects of the arbitration proceeding, including but not
              limited to the award of the arbitrator and compliance therewith,
              shall be strictly confidential. The parties agree to maintain
              confidentiality unless otherwise required by law. This paragraph
              shall not prevent a party from submitting to a court of law any
              information necessary to enforce this Agreement, to enforce an
              arbitration award, or to seek injunctive or equitable relief.
            </p>
          </li>
          <li>
            <u>Emergency Equitable Relief.</u>
            <p>
              Notwithstanding the foregoing, either party may seek emergency
              equitable relief before a state or federal court in order to
              maintain the status quo pending arbitration. A request for interim
              measures shall not be deemed a waiver of any other rights or
              obligations under this Arbitration Agreement.
            </p>
          </li>
          <li>
            <u>Claims Not Subject to Arbitration.</u>
            <p>
              Notwithstanding the foregoing, claims of defamation, violation of
              the Computer Fraud and Abuse Act, and infringement or
              misappropriation of the other party’s patent, copyright, trademark
              or trade secrets shall not be subject to this Arbitration
              Agreement.
            </p>
          </li>
        </ol>
        <h2>Termination and Suspension</h2>
        <p>
          We may terminate or limit your right to use the Site or Services in
          the event that we are investigating or believe that you have breached
          any provision of these Terms or our Privacy Policy by providing you
          with written or email notice. Such termination or limitation will be
          effective immediately upon delivery of such notice.
        </p>
        <p>
          If we terminate or limit your right to use the Site or Services, you
          are prohibited from registering and creating a new account under your
          name, a fake or borrowed name, or the name of any third party, even if
          you may be acting on behalf of the third party. As a Nonprofit, you
          are prohibited from additional funding from Every.org. Even after your
          right to use the Site is terminated or limited, these Terms will
          remain enforceable against you. Every.org reserves the right to take
          appropriate legal action, including but not limited to pursuing
          arbitration.
        </p>
        <p>
          Every.org reserves the right to modify or discontinue, temporarily or
          permanently, all or any portion of the Site and Services at its sole
          discretion. Every.org is not liable to you for any modification or
          discontinuance of all or any portion of the Site. We have the right to
          restrict anyone from using the Site or Services if we believe such
          person may threaten the safety and integrity of the Site, or if, in
          Every.org’s discretion, such restriction is necessary to address any
          other reasonable business concern.
        </p>
        <h2>Miscellaneous</h2>
        <p>
          <u>Entire Agreement.</u> These Terms and any other policies posted on
          the Site constitute the entire agreement and supersede all other
          agreements between Every.org and you relating to this subject matter.
        </p>
        <p>
          <u>Notices.</u> You consent to receive any agreements, notices,
          disclosures, and other communications (collectively, “Notices”) to
          which these Terms refer to electronically, including without
          limitation by electronic mail or by posting Notices on the Site. You
          agree that all Notices we provide to you electronically satisfy any
          legal requirement that such communications be in writing.
        </p>
        <p>
          <u>Governing Law and Jurisdiction.</u>{" "}
          <b>
            All claims arising out of or relating to these terms will be
            governed by California law, excluding {"California's"} conflict of
            laws rules, and will be litigated exclusively in the federal or
            state courts of San Francisco, California, USA; you consent to
            personal jurisdiction in those courts.
          </b>
        </p>
        <p>
          The Site is controlled and operated from the United States, and is not
          intended to subject Every.org to the laws or jurisdiction of any
          state, country or territory other than that of the United States. We
          do not represent or warrant that the Site is appropriate or available
          for use in any particular jurisdiction other than the United States.
          In choosing to access the Site or use the Services, you do so on your
          own initiative and at your own risk, and you are responsible for
          complying with all local laws, rules and regulations. We may limit the
          availability of the Site or Services, in whole or in part, to any
          person, geographic area or jurisdiction we choose, at any time and in
          our sole discretion.
        </p>
        <p>
          <u>Severability.</u> If any provision of these Terms is found to be
          unlawful, void, or unenforceable, the rest of the Terms will remain in
          effect.
        </p>
        <p>
          <u>Survival.</u> All provisions of these Terms which by their nature
          should survive termination will survive termination, including,
          without limitation, ownership provisions, warranty disclaimers,
          indemnity, and limitations of liability
        </p>
        <p>
          <u>Third Party Links.</u> Our Site may contain links to third party
          websites that may give you information about organizations and events
          related to what we do. These third-party websites are not run by us,
          and they are responsible for, and undertake to maintain, their own
          terms of use. These links do not constitute endorsement or association
          by Every.org with these third-party websites, their content, or their
          operators. We have no control over the content of third-party sites
          and accept no responsibility for them and hereby disclaim all
          liability related to them.
        </p>
        <p>
          <u>No Third-Party Beneficiaries.</u> These Terms control the
          relationship between Every.org and you. They do not create any
          third-party beneficiary rights.
        </p>
        <p>
          <u>No Agency.</u> No agency, partnership, or joint venture,
          employer-employee relationship is intended or created by these Terms.
        </p>
        <p>
          <u>No Waiver.</u> If you do not comply with these terms, and we don’t
          take action immediately, this doesn’t mean that we are giving up any
          rights that we may have (such as taking action in the future).
        </p>
        <p>
          <u>Changes to these Terms.</u> We reserve the right, at our sole
          discretion, to change, modify, add, or remove any portion of these
          Terms, in whole or in part, at any time. When we do, we will give you
          notice of the revised Terms by posting the revised Terms on the Site
          and by revising the “last updated” date at the top of this page.
        </p>
        <h2>Contact Us</h2>
        <p>
          If you have questions or concerns about these terms, <ContactUsLink />
          .
        </p>
        <TopLink data-tname="termsOfService--top" />
      </ArticlePageContent>
    </DefaultPageLayout>
  );
};

// Use default export to support React Lazy Loading.
export default TermsOfService;
